import { OpenInNew } from '@mui/icons-material';
// eslint-disable-next-line no-restricted-imports
import { Link as MUILink, type LinkProps } from '@mui/material';
import React, { forwardRef } from 'react';
import { Link as RouterLink, useLinkClickHandler } from 'react-router';
import {
  getHrefString,
  getLinkSecurityAttributes,
  isExternalLink,
  isMailToLink,
  validateHref,
} from '~/components/Link/linkUtils';
import type { RouteLocationOptions } from '~/hooks/useRouter';

// Type for props that we want to accept
export interface CustomLinkProps extends Omit<LinkProps, 'href'> {
  href: string | RouteLocationOptions;
  replace?: boolean;
  preventScrollReset?: boolean;
  children: React.ReactNode;
  underline?: LinkProps['underline'];
  hideExternalIcon?: boolean;
}

// Create component with proper type checking
export const Link = forwardRef<HTMLAnchorElement, CustomLinkProps>(
  (props, ref) => {
    const {
      href,
      replace,
      preventScrollReset,
      onClick,
      children,
      target,
      rel,
      hideExternalIcon,
      ...muiProps
    } = props;
    const hrefString = getHrefString(href);
    validateHref(hrefString);
    const finalTargetAttribute =
      target || (isExternalLink(hrefString) ? '_blank' : undefined);

    const showExternalIcon =
      !hideExternalIcon &&
      finalTargetAttribute === '_blank' &&
      !hrefString.startsWith('mailto:');

    const handleClick = useLinkClickHandler(
      hrefString.replace(new RegExp(`^${import.meta.env.VITE_ORIGIN}`), ''),
      {
        replace,
        preventScrollReset,
        target: finalTargetAttribute,
      }
    );

    return (
      <MUILink
        {...muiProps}
        viewTransition
        ref={ref}
        target={finalTargetAttribute}
        referrerPolicy="unsafe-url"
        rel={getLinkSecurityAttributes(hrefString, rel)}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented && !isMailToLink(hrefString)) {
            handleClick(event);
          }
        }}
        component={RouterLink}
        {...{ to: hrefString, replace, preventScrollReset }}>
        {children}
        {showExternalIcon && <OpenInNew sx={{ fontSize: 12, ml: 0 }} />}
      </MUILink>
    );
  }
);

Link.displayName = 'CustomLink';
